import { gql, useMutation, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { trackProductEvent } from '../../analytics';
import { getIntegrationErrorMessage } from '../../integrations/error-message';
import {
  Button,
  ButtonLink,
  ErrorBanner,
  Spinner,
} from '../../ui';
import Modal from './modal';

const SYNC_STATE = gql`
  query TemplateSyncState($id: ID) {
    getTemplate(id: $id) {
      id
      espSyncs {
        provider
        remoteTemplateId
        remoteVersionId
        lastSyncedAt
        lastSyncStatus
        lastSyncError
      }
    }
    listIntegrations {
      provider
      status
      lastValidationError
    }
  }
`;

const SYNC_TEMPLATE = gql`
  mutation SyncTemplateToIntegrations(
    $templateId: ID!
    $providers: [String!]!
  ) {
    syncTemplateToIntegrations(
      input: { templateId: $templateId, providers: $providers }
    ) {
      provider
      success
      error
      sync {
        provider
        remoteTemplateId
        remoteVersionId
        lastSyncedAt
        lastSyncStatus
        lastSyncError
      }
    }
  }
`;

const providerNames: Record<string, string> = {
  sendgrid: 'SendGrid',
  aws_ses: 'Amazon SES',
};

const providerName = (provider: string) =>
  providerNames[provider] || provider;

export default function SyncTemplateModal({
  isOpen,
  toggle,
  templateId,
  hasUnsavedChanges,
  onRequestSave,
}: {
  isOpen: boolean;
  toggle: () => void;
  templateId: string;
  hasUnsavedChanges: boolean;
  onRequestSave: () => Promise<boolean>;
}) {
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
  const [formError, setFormError] = useState('');
  const [waitingForSave, setWaitingForSave] = useState(false);
  const { data, loading, error, refetch } = useQuery(SYNC_STATE, {
    variables: { id: templateId },
    skip: !isOpen || !templateId,
    ssr: false,
    fetchPolicy: 'network-only',
  });
  const [syncTemplate, { loading: syncing }] = useMutation(SYNC_TEMPLATE);

  const activeIntegrations =
    data?.listIntegrations?.filter(
      (integration) => integration.status === 'active'
    ) || [];

  useEffect(() => {
    if (!isOpen) return;
    setFormError('');
    setWaitingForSave(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedProviders(
      activeIntegrations.map((integration) => integration.provider)
    );
  }, [data?.listIntegrations, isOpen]);

  const performSync = async () => {
    if (!selectedProviders.length) {
      setFormError('Select at least one active integration.');
      return;
    }

    setFormError('');
    trackProductEvent('Template Sync Attempted', {
      template_id: templateId,
      providers: selectedProviders.join(','),
    });
    try {
      const response = await syncTemplate({
        variables: {
          templateId,
          providers: selectedProviders,
        },
      });
      const results = response.data?.syncTemplateToIntegrations || [];
      results.forEach((result) => {
        trackProductEvent(
          result.success
            ? 'Template Sync Succeeded'
            : 'Template Sync Failed',
          {
            template_id: templateId,
            provider: result.provider,
            ...(result.error ? { error: result.error } : {}),
          }
        );
      });
      await refetch();

      const failures = results.filter((result) => !result.success);
      if (failures.length) {
        setFormError(
          failures.map((result) => result.error).filter(Boolean).join(' ')
        );
        toast.error('Template sync failed.');
        return;
      }

      toast.success('Template synced.');
      toggle();
    } catch (syncError) {
      const message = getIntegrationErrorMessage(
        syncError?.message || 'Unable to sync template.'
      );
      setFormError(message);
      trackProductEvent('Template Sync Failed', {
        template_id: templateId,
        providers: selectedProviders.join(','),
        error: message,
      });
      toast.error('Template sync failed.');
    }
  };

  const handleSync = async () => {
    if (hasUnsavedChanges) {
      setWaitingForSave(true);
      const saved = await onRequestSave();
      setWaitingForSave(false);
      if (!saved) {
        setFormError('Save the latest template changes before syncing.');
        return;
      }
    }
    await performSync();
  };

  const syncs = data?.getTemplate?.espSyncs || [];

  return (
    <Modal toggle={toggle} isOpen={isOpen} size="sm:max-w-lg" closeButton>
      <div className="text-left">
        <h3
          className="text-base font-semibold tracking-tight text-neutral-900"
          id="modal-headline"
        >
          Sync to ESP
        </h3>
        <p className="mt-2 text-sm text-neutral-600">
          Push the latest saved template to your active integrations.
        </p>

        {loading ? (
          <div className="flex justify-center py-10">
            <Spinner className="h-8 w-8" />
          </div>
        ) : activeIntegrations.length ? (
          <div className="mt-5 space-y-2">
            {activeIntegrations.map((integration) => {
              const sync = syncs.find(
                (item) => item.provider === integration.provider
              );
              const checked = selectedProviders.includes(
                integration.provider
              );
              return (
                <label
                  key={integration.provider}
                  className="flex cursor-pointer items-start gap-3 rounded-control border border-subtle p-3"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(event) =>
                      setSelectedProviders((current) =>
                        event.target.checked
                          ? [...current, integration.provider]
                          : current.filter(
                              (provider) =>
                                provider !== integration.provider
                            )
                      )
                    }
                    className="mt-0.5 rounded border-standard text-blue focus:ring-blue/30"
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-neutral-900">
                      {providerName(integration.provider)}
                    </span>
                    <span className="mt-0.5 block text-xs text-neutral-500">
                      {sync?.lastSyncedAt
                        ? `Last synced ${new Date(
                            sync.lastSyncedAt
                          ).toLocaleString()}`
                        : 'Not synced yet'}
                    </span>
                    {sync?.lastSyncError && (
                      <span className="mt-1 block text-xs text-red-600">
                        {sync.lastSyncError}
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        ) : (
          <div className="mt-5 rounded-control border border-subtle p-4">
            <p className="text-sm text-neutral-600">
              Connect and enable an integration before syncing.
            </p>
            <ButtonLink href="/integrations" className="mt-3" size="sm">
              Manage integrations
            </ButtonLink>
          </div>
        )}

        {(error || formError) && (
          <ErrorBanner
            title={
              formError || getIntegrationErrorMessage(error?.message)
            }
          />
        )}
        <div className="mt-6 flex gap-2">
          <Button
            tag="button"
            type="button"
            onClick={() => void handleSync()}
            disabled={
              syncing ||
              waitingForSave ||
              loading ||
              !activeIntegrations.length
            }
          >
            {waitingForSave
              ? 'Saving...'
              : syncing
                ? 'Syncing...'
                : 'Sync'}
          </Button>
          <Button tag="button" type="button" variant="ghost" onClick={toggle}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
